import "./BannerWing.css";

/* ---------------------------------------------------------------------------
   Banner wings.

   The banner artwork is a fixed 16:9 raster, so on wider viewports it is shown
   at its natural size rather than stretched — which leaves a gap either side.
   These panels fill that gap with vector artwork that continues each banner's
   own design outward: the same field colour, the same foot band at the same
   height, and motifs lifted from the banner itself.

   Band positions live in CSS so they line up with the photograph exactly. The
   SVG carries only decoration, which can be cropped safely, and fades out
   towards the photograph so there is no visible seam.

   Colours are sampled from the banners' own edge pixels — see the comments in
   BannerWing.css before changing any of them.
--------------------------------------------------------------------------- */

const Healthcare = ({ side }) => (
  <svg
    className="wing-art"
    viewBox="0 0 320 900"
    preserveAspectRatio={side === "left" ? "xMinYMid slice" : "xMaxYMid slice"}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id={`wdisc-${side}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1d6fd0" />
        <stop offset="100%" stopColor="#0b3f96" />
      </linearGradient>
    </defs>

    {/* the big blue disc, echoing the one behind the students */}
    <circle
      cx={side === "left" ? -40 : 360}
      cy="300"
      r="250"
      fill={`url(#wdisc-${side})`}
      opacity="0.14"
    />
    <circle
      cx={side === "left" ? -10 : 330}
      cy="205"
      r="110"
      fill="#1d6fd0"
      opacity="0.1"
    />

    {/* the banner's hatch marks */}
    <g stroke="#1d6fd0" strokeWidth="3" opacity="0.16">
      {[0, 1, 2, 3, 4, 5].map((n) => (
        <line
          key={n}
          x1={side === "left" ? 30 + n * 13 : 150 + n * 13}
          y1={120}
          x2={side === "left" ? 52 + n * 13 : 172 + n * 13}
          y2={196}
        />
      ))}
    </g>

    {/* medical crosses, the motif dotted across the banner's sky */}
    <g fill="#1d6fd0" opacity="0.18">
      {[
        [58, 430, 15],
        [206, 520, 11],
        [120, 636, 9],
        [252, 300, 8],
        [30, 250, 10],
      ].map(([x, y, s], n) => (
        <path
          key={n}
          d={`M${x - s / 3},${y - s} h${(s * 2) / 3} v${(s * 2) / 3} h${s / 1.5} v${s / 1.5} h-${s / 1.5} v${(s * 2) / 3} h-${(s * 2) / 3} v-${(s * 2) / 3} h-${s / 1.5} v-${s / 1.5} h${s / 1.5} Z`}
        />
      ))}
    </g>

    {/* the fine dot field along the outer margin */}
    <g fill="#1d6fd0" opacity="0.2">
      {Array.from({ length: 7 }).map((_, r) =>
        Array.from({ length: 4 }).map((__, c) => (
          <circle
            key={`${r}-${c}`}
            cx={(side === "left" ? 16 : 244) + c * 20}
            cy={560 + r * 20}
            r="2.4"
          />
        ))
      )}
    </g>
  </svg>
);

const ManagementLaw = ({ side }) => (
  <svg
    className="wing-art"
    viewBox="0 0 320 900"
    preserveAspectRatio={side === "left" ? "xMinYMid slice" : "xMaxYMid slice"}
    aria-hidden="true"
  >
    <defs>
      <radialGradient id={`wbokeh-${side}`}>
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* out-of-focus light, continuing the blurred campus behind the students */}
    {[
      [70, 190, 130],
      [250, 380, 165],
      [40, 560, 110],
      [230, 690, 95],
      [150, 90, 80],
    ].map(([cx, cy, r], n) => (
      <circle key={n} cx={cx} cy={cy} r={r} fill={`url(#wbokeh-${side})`} opacity="0.55" />
    ))}

    {/* warm shapes picking up the brick and foliage tones either side */}
    <circle cx={side === "left" ? 20 : 300} cy="300" r="190" fill="#d8c3a5" opacity="0.16" />
    <circle cx={side === "left" ? 60 : 260} cy="660" r="150" fill="#b9c7a4" opacity="0.14" />
  </svg>
);

const ART = { healthcare: Healthcare, "management-law": ManagementLaw };

const BannerWing = ({ variant, side }) => {
  const Art = ART[variant] || Healthcare;
  return (
    <div className={`wing wing--${variant} wing--${side}`} aria-hidden="true">
      <Art side={side} />
      {/* the torn paper edge that separates field from foot band */}
      {variant === "healthcare" && (
        <svg className="wing-tear" viewBox="0 0 320 60" preserveAspectRatio="none" aria-hidden="true">
          <path
            d="M0,34 L26,20 L52,33 L78,17 L104,31 L130,15 L156,30 L182,16 L208,32 L234,18 L260,33 L286,19 L320,32 L320,60 L0,60 Z"
            fill="#ffffff"
          />
        </svg>
      )}
    </div>
  );
};

export default BannerWing;
